import {
  Box,
  Link,
  Grid,
  Dialog,
  Button,
  Divider,
  TextField,
  Typography,
  IconButton,
  InputLabel,
  FormControl,
  Autocomplete,
  DialogActions,
  DialogContent
} from '@mui/material';
import { toast } from 'react-toastify';
import React, { useState, useEffect } from 'react';
import LoadingButton from '@mui/lab/LoadingButton';
import { EditIcon, DeleteIcon } from '../../../utils/Icons';
import CommonModalHeader from '../../../components/DialogUi/CommonModalHeader';
import { API } from '../../../api';
import { getUserDetails } from '../../../utils/orgName';
import { userActions } from '../../../redux/slices/user-profile/userProfieSlice';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';

interface AreasofExpertiseModalProps {
  openPopup: boolean;
  handleOpenPopup: (flag: boolean) => void;
}

interface Expertise {
  id: number;
  isEditing: boolean;
  objective: string;
}

const AreasofExpertiseModal: React.FC<AreasofExpertiseModalProps> = ({ openPopup, handleOpenPopup }) => {
  const [expertiseList, setExpertiseList] = useState<Expertise[]>([{ id: Date.now(), objective: '', isEditing: true }]);
  const [submitLoader, setSubmitLoader] = useState(false);
  const [objectivesList, setObjectivesList] = useState([]);
  const [loadingObjectives, setLoadingObjectives] = useState(false);
  const { orgId } = getUserDetails();
  const dispatch: any = useAppDispatch();
  const existingLearningObjectives = useAppSelector((state) => state.userProfile.data?.learningObjectives || []);
  useEffect(() => {
    if (openPopup) {
      fetchLearningObjectives();
      //   if (expertiseList.length === 0) {
      setExpertiseList([{ id: Date.now(), objective: '', isEditing: true }]);
      //   }
    }
  }, [openPopup]);

  const fetchLearningObjectives = async () => {
    if (!orgId) return;

    setLoadingObjectives(true);
    try {
      const response = await API.getUserLearningObjectives({ orgId });
      const filteredObjectives = (response.data.data || []).filter(
        (obj: any) => !existingLearningObjectives.some((existing) => existing.objective === obj.objective)
      );
      setObjectivesList(filteredObjectives);
    } catch (error) {
      toast.error('Failed to fetch learning objectives');
      console.error(error);
    } finally {
      setLoadingObjectives(false);
    }
  };

  const handleChange = (id: number, newValue: string) => {
    setExpertiseList((prev) => prev.map((item) => (item.id === id ? { ...item, objective: newValue } : item)));
  };

  const handleBlur = (id: number) => {
    setExpertiseList((prev) => prev.map((item) => (item.id === id ? { ...item, isEditing: false } : item)));
  };
  const handleSubmit = async () => {
    const validExpertise = expertiseList.filter((item) => item.objective.trim());

    if (validExpertise.length === 0) {
      toast.error('Please add at least one area of expertise');
      return;
    }

    setSubmitLoader(true);

    try {
      const response = await API.getUserLearningObjectives({ orgId });
      const allObjectives = response?.data?.data || [];

      const objectivesToSave = validExpertise.map((item) => {
        const existingObj = allObjectives.find((dbObj: any) => dbObj.objective === item.objective.trim());
        return existingObj
          ? { id: existingObj.id, objective: existingObj.objective }
          : { objective: item.objective.trim() };
      });
      const existingUserObjectives = existingLearningObjectives.map((obj) => ({
        id: obj.id,
        objective: obj.objective
      }));
      const combinedObjectives = [...existingUserObjectives];
      objectivesToSave.forEach((newObj: any) => {
        const exists = combinedObjectives.some((existingObj) => existingObj.objective === newObj.objective);
        if (!exists) {
          combinedObjectives.push(newObj);
        }
      });
      const payload = {
        learningObjectives: combinedObjectives
      };

      const saveResponse = await API.saveLearningObjectives(payload);
      if (saveResponse.status === 200) {
        toast.success('Areas of expertise saved successfully!');
        dispatch(userActions.updateObjectives(combinedObjectives));
        handleOpenPopup(false);
      } else {
        toast.error('Failed to save areas of expertise');
      }
    } catch (error) {
      console.error('Error saving expertise:', error);
      toast.error('An error occurred while saving');
    } finally {
      setSubmitLoader(false);
    }
  };

  const handleClose = () => {
    handleOpenPopup(false);
  };

  const getAvailableOptions = () => {
    const currentSelected = expertiseList.map((item) => item.objective.trim());
    return objectivesList
      .map((obj: any) => obj.objective)
      .filter(
        (option) =>
          option &&
          !currentSelected.includes(option) &&
          !existingLearningObjectives.some((obj) => obj.objective === option)
      );
  };

  return (
    <Dialog open={openPopup} onClose={handleClose} fullWidth>
      <CommonModalHeader title="Areas of Expertise" handleCloseModal={handleClose} />

      <DialogContent>
        <Grid container>
          {expertiseList.map((item, index) => (
            <Grid item xs={12} key={item.id}>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <InputLabel>Area of Expertise</InputLabel>
              </Box>

              <Box>
                {item.isEditing || (index === 0 && !item.objective.trim()) ? (
                  <FormControl fullWidth>
                    <Autocomplete
                      freeSolo
                      options={getAvailableOptions()}
                      loading={loadingObjectives}
                      value={item.objective}
                      inputValue={item.objective}
                      onInputChange={(event, newInputValue) => handleChange(item.id, newInputValue)}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          variant="outlined"
                          size="small"
                          placeholder="Select or enter expertise"
                          autoFocus={index === 0}
                        />
                      )}
                    />
                  </FormControl>
                ) : (
                  <Typography variant="subtitle2" color="primary">
                    {item.objective}
                  </Typography>
                )}
              </Box>
              <Divider className="my-20" />
            </Grid>
          ))}
        </Grid>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} variant="outlined" fullWidth>
          Cancel
        </Button>
        <LoadingButton onClick={handleSubmit} variant="contained" loading={submitLoader} fullWidth>
          Save
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
};

export default AreasofExpertiseModal;
